import React, { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CartesianGrid, Line, LineChart, Tooltip, XAxis, YAxis } from 'recharts';
import StableResponsiveContainer from '../general/stable-responsive-container/stable-responsive-container.component';
import { AthleteAttendanceTeam } from '../../models/attendance.models';
import {
  buildPeriodChartData,
  formatAttendancePercent,
  getRingDashArray,
  getTeamCardTheme,
  getTeamInitials,
  getTeamLineColor,
  getTeamPeriodAttendance,
  getTeamPeriodAttendanceOrDefault,
  hasAttendanceData,
  PeriodChartRow,
} from './team-attendance-breakdown.helpers';
import TeamAttendancePager from './team-attendance-pager.component';
import './team-attendance-breakdown.component.css';

interface AttendanceProgressRingProps {
  percent: number;
  size?: number;
  ringColor?: string;
  ringTrack?: string;
  labelClassName?: string;
}

export const AttendanceProgressRing: FC<AttendanceProgressRingProps> = ({
  percent,
  size = 48,
  ringColor = '#2563EB',
  ringTrack = '#F2F2F7',
  labelClassName = 'tab-progress-ring-label',
}) => (
  <div className="tab-progress-ring" style={{ width: size, height: size }}>
    <svg width={size} height={size} viewBox="0 0 48 48">
      <circle cx="24" cy="24" r="19.5" fill="none" stroke={ringTrack} strokeWidth="4.5" />
      <circle
        cx="24"
        cy="24"
        r="19.5"
        fill="none"
        stroke={ringColor}
        strokeWidth="4.5"
        strokeLinecap="round"
        strokeDasharray={getRingDashArray(percent)}
        transform="rotate(-90 24 24)"
        style={{ transition: 'stroke-dasharray 0.6s ease' }}
      />
    </svg>
    <div className={labelClassName}>{formatAttendancePercent(percent)}</div>
  </div>
);

interface TeamAvatarProps {
  team: AthleteAttendanceTeam;
  themeIndex: number;
  size?: 'large' | 'compact';
}

const TeamAvatar: FC<TeamAvatarProps> = ({ team, themeIndex, size = 'large' }) => {
  const theme = getTeamCardTheme(team.color, themeIndex);
  const isCompact = size === 'compact';

  return (
    <div className={isCompact ? 'tab-period-tile-avatar' : 'tab-team-avatar'} style={{ background: theme.avatarBg }}>
      {team.badgeImage ? (
        <img src={team.badgeImage} alt="" />
      ) : (
        <span className={isCompact ? 'tab-period-tile-initials' : 'tab-team-avatar-initials'}>
          {getTeamInitials(team.teamName)}
        </span>
      )}
    </div>
  );
};

interface AttendanceMetricRowProps {
  label: string;
  percent: number;
  ringColor: string;
  ringTrack: string;
}

const AttendanceMetricRow: FC<AttendanceMetricRowProps> = ({ label, percent, ringColor, ringTrack }) => (
  <div className="tab-metric-row">
    <AttendanceProgressRing
      percent={percent}
      ringColor={ringColor}
      ringTrack={ringTrack}
      labelClassName="tab-progress-ring-label"
    />
    <div className="tab-metric-content">
      <div className="tab-metric-label">{label}</div>
      <div className="tab-metric-bar">
        <div className="tab-metric-bar-fill" style={{ width: `${Math.min(percent, 100)}%`, background: ringColor }} />
      </div>
    </div>
  </div>
);

interface TeamAttendanceCardProps {
  team: AthleteAttendanceTeam;
  themeIndex: number;
  onClick: () => void;
}

export const TeamAttendanceCard: FC<TeamAttendanceCardProps> = ({ team, themeIndex, onClick }) => {
  const theme = getTeamCardTheme(team.color, themeIndex);

  return (
    <div className="tab-team-card" style={{ background: theme.cardBg }} onClick={onClick}>
      <div className="tab-team-card-header">
        <div className="tab-team-card-identity">
          <TeamAvatar team={team} themeIndex={themeIndex} />
          <div className="tab-team-name">{team.teamName}</div>
        </div>
      </div>
      <div className="tab-team-card-metrics">
        <div className="tab-team-card-metrics-inner">
          <AttendanceMetricRow
            label={`Team Sessions - ${team.totalCompleted}/${team.totalAssigned}`}
            percent={team.sessionPercent}
            ringColor={theme.ringColor}
            ringTrack={theme.ringTrack}
          />
          <AttendanceMetricRow
            label={`Practice Attendance - ${team.totalAttended}/${team.totalMarkedAttended}`}
            percent={team.practicePercent}
            ringColor={theme.ringColor}
            ringTrack={theme.ringTrack}
          />
        </div>
      </div>
    </div>
  );
};

interface PeriodTeamAttendanceTileProps {
  team: AthleteAttendanceTeam;
  periodIndex: number;
  themeIndex: number;
}

export const PeriodTeamAttendanceTile: FC<PeriodTeamAttendanceTileProps> = ({ team, periodIndex, themeIndex }) => {
  const periodData = getTeamPeriodAttendanceOrDefault(team, periodIndex);

  const theme = getTeamCardTheme(team.color, themeIndex);

  return (
    <div className="tab-period-tile" style={{ background: theme.cardBg }}>
      <TeamAvatar team={team} themeIndex={themeIndex} size="compact" />
      <div className="tab-period-tile-content">
        <div className="tab-period-tile-name">{team.teamName}</div>
        <div className="tab-period-tile-metrics">
          <div className="tab-period-tile-metric">
            <span className="tab-period-tile-metric-value">{formatAttendancePercent(periodData.practicePercent)}</span>
            <span className="tab-period-tile-metric-label">
              {periodData.attended}/{periodData.markedAttended}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

interface AttendancePeriodChartProps {
  teams: AthleteAttendanceTeam[];
  data: PeriodChartRow[];
}

const AttendancePeriodChart: FC<AttendancePeriodChartProps> = ({ teams, data }) => {
  const teamsKey = useMemo(() => teams.map((team) => team.teamId).join(','), [teams]);
  const [activeTeamIds, setActiveTeamIds] = useState<Set<number>>(() => new Set(teams.map((team) => team.teamId)));

  useEffect(() => {
    setActiveTeamIds(new Set(teams.map((team) => team.teamId)));
  }, [teamsKey]);

  const toggleTeam = (teamId: number) => {
    setActiveTeamIds((prev) => {
      const next = new Set(prev);
      if (next.has(teamId)) {
        next.delete(teamId);
      } else {
        next.add(teamId);
      }
      return next;
    });
  };

  if (data.length === 0 || teams.length === 0) {
    return <div className="tab-chart-empty">No period attendance data</div>;
  }

  const visibleTeams = teams.filter((team) => activeTeamIds.has(team.teamId));

  return (
    <>
      <div className="tab-team-toggles">
        {teams.map((team, index) => {
          const isActive = activeTeamIds.has(team.teamId);
          const color = getTeamLineColor(team, index);

          return (
            <button
              key={team.teamId}
              type="button"
              className={`tab-team-toggle ${isActive ? 'active' : ''}`}
              onClick={() => toggleTeam(team.teamId)}
            >
              <span className="tab-team-toggle-dot" style={{ background: color, opacity: isActive ? 1 : 0.35 }} />
              {team.teamName}
            </button>
          );
        })}
      </div>

      <div className="tab-chart-container">
        {visibleTeams.length === 0 ? (
          <div className="tab-chart-empty">Select a team to view attendance trends</div>
        ) : (
          <StableResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: '#6B7280' }}
                tickLine={false}
                axisLine={{ stroke: '#E5E7EB' }}
                interval="preserveStartEnd"
                minTickGap={16}
              />
              <YAxis
                domain={[0, 100]}
                tick={{ fontSize: 10, fill: '#6B7280' }}
                tickLine={false}
                axisLine={false}
                width={48}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) {
                    return null;
                  }

                  return (
                    <div
                      style={{
                        background: '#fff',
                        border: '1px solid #E5E7EB',
                        borderRadius: 8,
                        padding: '8px 12px',
                        fontSize: 12,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                      }}
                    >
                      <div style={{ fontWeight: 600, marginBottom: 6, color: '#111827' }}>{label}</div>
                      {payload
                        .filter((entry) => activeTeamIds.has(Number(entry.dataKey)))
                        .map((entry) => {
                          const team = teams.find((t) => String(t.teamId) === String(entry.dataKey));
                          const periodIndex = Number((entry.payload as PeriodChartRow)?.periodIndex);
                          const periodItem = team ? getTeamPeriodAttendance(team, periodIndex) : undefined;

                          return (
                            <div
                              key={String(entry.dataKey)}
                              style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}
                            >
                              <span
                                style={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  background: entry.color,
                                  flexShrink: 0,
                                }}
                              />
                              <span style={{ color: '#6B7280', minWidth: 72 }}>{team?.teamName ?? entry.dataKey}</span>
                              <span style={{ fontWeight: 600, color: '#111827' }}>
                                {formatAttendancePercent(Number(entry.value))}
                                {periodItem ? ` · ${periodItem.attended}/${periodItem.markedAttended}` : ''}
                              </span>
                            </div>
                          );
                        })}
                    </div>
                  );
                }}
              />
              {visibleTeams.map((team) => {
                const index = teams.findIndex((t) => t.teamId === team.teamId);

                return (
                  <Line
                    key={team.teamId}
                    type="monotone"
                    dataKey={String(team.teamId)}
                    stroke={getTeamLineColor(team, index)}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                    isAnimationActive={false}
                    connectNulls
                  />
                );
              })}
            </LineChart>
          </StableResponsiveContainer>
        )}
      </div>
    </>
  );
};

interface TeamAttendanceBreakdownProps {
  attendanceTeams: AthleteAttendanceTeam[] | null;
  loading: boolean;
}

const TeamAttendanceBreakdown: FC<TeamAttendanceBreakdownProps> = ({ attendanceTeams, loading }) => {
  const navigate = useNavigate();

  const periodChartData = useMemo(() => buildPeriodChartData(attendanceTeams ?? []), [attendanceTeams]);

  const teams = attendanceTeams ?? [];

  if (loading) {
    return (
      <div className="tab-card">
        <div className="tab-header">
          <div className="tab-title">Team Attendance</div>
          <div className="tab-subtitle">Annual attendance rates across your teams</div>
        </div>
        <div className="tab-loading">
          <div className="tab-skeleton-grid">
            <div className="tab-skeleton-card" />
            <div className="tab-skeleton-card" />
          </div>
          <div className="tab-skeleton-chart" />
        </div>
      </div>
    );
  }

  if (!hasAttendanceData(attendanceTeams)) {
    return (
      <div className="tab-card">
        <div className="tab-header">
          <div className="tab-title">Team Attendance</div>
          <div className="tab-subtitle">Annual attendance rates across your teams</div>
        </div>
        <div className="tab-empty">No team attendance data for this season</div>
      </div>
    );
  }

  return (
    <div className="tab-card">
      <div className="tab-header">
        <div className="tab-title">Team Attendance</div>
        <div className="tab-subtitle">Annual attendance rates across your teams</div>
      </div>
      <div className="tab-content">
        <TeamAttendancePager
          items={teams}
          gridClassName="tab-team-grid"
          getItemKey={(team) => team.teamId}
          renderItem={(team, index) => (
            <TeamAttendanceCard
              key={team.teamId}
              team={team}
              themeIndex={index}
              onClick={() => navigate(`/teams/${team.teamId}/dashboard`)}
            />
          )}
        />
        <div className="tab-period-section">
          <div className="tab-period-title">Attendance by Period</div>
          <div className="tab-period-subtitle">Team practice attendance rates across the season</div>
          <AttendancePeriodChart teams={teams} data={periodChartData} />
        </div>
      </div>
    </div>
  );
};

export default TeamAttendanceBreakdown;
